import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { handlePhonePaste } from '@shared/__HACKS'
import { useSaveFeedbackMutation } from '@shared/generated'
import Button from '@shared/ui/button'
import Checkbox from '@shared/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@shared/ui/form'
import Input from '@shared/ui/input'
import MaskedInput from '@shared/ui/input-mask'
import PolicyLink from '@shared/ui/policy'
import Textarea from '@shared/ui/text-area'
import { PHONE_MASK_LENGTH } from '@shared/validation'

import styles from './ContactForm.module.scss'

export default function ContactForm () {
  const { t: tc } = useTranslation('contacts')
  const { t: tv } = useTranslation('validation')
  const [ fetchSaveShopFeedback ] = useSaveFeedbackMutation()

  const REQUIRED_MESSAGE = tv('required')

  const formSchema = z.object({
    phone: z.string().min(PHONE_MASK_LENGTH, {
      message: tv('phone'),
    }),
    name: z.string().min(1, {
      message: REQUIRED_MESSAGE,
    }),
    email: z
      .string()
      .optional()
      .refine((val) => !val || z.string().email().safeParse(val).success, {
        message: tv('email'),
      }),
    comment: z.string(),
    policy: z.boolean().refine((val) => val === true, {
      message: REQUIRED_MESSAGE,
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: '',
      name: '',
      email: '',
      comment: '',
      policy: false,
    },
  })

  const onSubmit = async (_values: z.infer<typeof formSchema>) => {
    try {
      await fetchSaveShopFeedback({
        variables: {
          feedbackInfo: {
            brand_id: '1',
            order_id: null,
            shop_id: null,
            images: [],
            need_call: true,
          },
        },
      })
    }
    catch (error) {
      console.log(error)
    }
  }

  return (
    <div className={styles.form}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className={styles.form__inner}
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={tc('form.name')}
                    className={styles.form__input}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className={styles.form__row}>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <MaskedInput
                      {...field}
                      placeholder={tc('form.phone')}
                      onPaste={handlePhonePaste}
                      className={styles.form__input}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={tc('form.email')}
                      className={styles.form__input}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={tc('form.message')}
                    className={styles.form__textarea}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="policy"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Checkbox
                    {...field}
                    label={<PolicyLink buttonText={tc('form.submit')} />}
                    value={String(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                    checkboxSize="extra_small"
                    className={styles.form__checkbox}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            size="large"
            className={styles.form__submit}
          >
            {tc('form.submit')}
          </Button>
        </form>
      </Form>
    </div>
  )
}
